import { useCallback } from 'react';

import { MarginPositionsQuery, MarginPositionStatus } from '@swyftx/margin/build/types';

import { api } from '@shared/api';
import { TimesEnum } from '@shared/enums';
import { UserStore } from '@shared/store';

import { useInfiniteQuery } from '@tanstack/react-query';

import { useMarginPositionsStore } from './useMarginPositionsStore';
import { mapPositions } from '../../utils';
import { useMarginAccount } from '../account';

type Props = {
  pageSize?: number;
  status?: MarginPositionStatus[];
  sortBy?: MarginPositionsQuery['sortBy'];
  sortDir?: MarginPositionsQuery['sortDir'];
};

const useFetchMarginPositions = (
  { pageSize = 250, status = [], sortBy, sortDir }: Props = {
    pageSize: 250,
    status: [],
    sortBy: 'lastUserAction',
    sortDir: 'desc',
  },
) => {
  const { account } = useMarginAccount();
  const { getUniqueIdentifier } = UserStore.useUserStore;
  const { updatePositions } = useMarginPositionsStore();

  const fetchPositions = useCallback(
    async (pageNumber = 1) => {
      const response = await api.endpoints.getPositions({
        params: { swyftxUserId: getUniqueIdentifier(), traderAccountId: account?.traderAccountId },
        query: {
          pageNumber,
          pageSize,
          status: encodeURI(status.join(',')),
          sortBy,
          sortDir,
        },
      });

      const positions = mapPositions(response.data.positions);

      updatePositions(positions);

      return {
        positions,
        page: response.data.positions.length === pageSize ? pageNumber : undefined,
      };
    },
    [getUniqueIdentifier, account?.traderAccountId, pageSize, status, sortBy, sortDir, updatePositions],
  );

  return useInfiniteQuery({
    queryKey: ['margin-positions', getUniqueIdentifier(), account?.traderAccountId, status, pageSize, sortBy, sortDir],
    queryFn: ({ pageParam = 1 }) => fetchPositions(pageParam),
    keepPreviousData: true,
    staleTime: TimesEnum.SECOND * 60,
    retry: 3,
    networkMode: 'always',
    refetchOnWindowFocus: false,
    enabled: !!getUniqueIdentifier() && !!account?.traderAccountId,
    getNextPageParam: (lastPage) => (lastPage.page !== undefined ? lastPage.page + 1 : undefined),
    getPreviousPageParam: (lastPage) => (lastPage.page !== undefined ? lastPage.page - 1 : undefined),
  });
};

export { useFetchMarginPositions };
